// One-line summaries for the generate screen's detail rows and the desktop
// summary sentence. Pure, so the copy can be tested without rendering.

import { TARGET_AREA_PRESETS } from "../config/targets";
import { FORMAT_OPTIONS, WORKOUT_TYPE_OPTIONS } from "../config/workoutOptions";
import { resolveSegmentMode } from "./workoutPlanUtils";
import { autoFormatFor, hasSelector } from "./sessionFormat";

const sameSet = (a, b) =>
  a.length === b.length && a.every((x) => b.includes(x));

const optionLabel = (options, value) =>
  options.find((o) => o.value === value)?.label || value;

/** The muscle preset a list exactly matches, or null. */
export const matchingMusclePreset = (targetAreas) => {
  const list = Array.isArray(targetAreas) ? targetAreas : [];
  if (list.length === 0) return null;
  const hit = Object.entries(TARGET_AREA_PRESETS).find(([, muscles]) =>
    sameSet(muscles, list),
  );
  return hit ? hit[0] : null;
};

export const musclesSummary = (targetAreas) => {
  const list = Array.isArray(targetAreas) ? targetAreas : [];
  if (list.length === 0) return "No focus";
  const preset = matchingMusclePreset(list);
  if (preset) return preset;
  if (list.length <= 2) return list.join(", ");
  return `${list.slice(0, 2).join(", ")} +${list.length - 2}`;
};

export const kitSummary = (locationLabel, equipment) => {
  const list = (Array.isArray(equipment) ? equipment : []).filter(
    (e) => e && e !== "Bodyweight",
  );
  const kit =
    list.length === 0
      ? "bodyweight only"
      : `${list.length} piece${list.length === 1 ? "" : "s"} of kit`;
  return locationLabel ? `${locationLabel} · ${kit}` : kit;
};

/** What the format row says, given who is choosing it. */
export const formatSummary = ({ workoutTypes, format, noFaff }) => {
  if (hasSelector(workoutTypes)) {
    const list = Array.isArray(workoutTypes) ? workoutTypes : [];
    const who = list.includes("next") ? "What's next" : "Surprise me";
    return `Set by ${who}`;
  }
  const chosen = format || "auto";
  if (chosen !== "auto") return optionLabel(FORMAT_OPTIONS, chosen);
  const auto = autoFormatFor({ goals: workoutTypes, noFaff });
  return auto ? `Auto · ${optionLabel(FORMAT_OPTIONS, auto)}` : "Auto";
};

const segmentWord = (config, segment, label) => {
  const mode = resolveSegmentMode(config, segment);
  if (mode === "off") return `no ${label}`;
  if (mode === "on") {
    const mins = config[`${segment}Duration`] || 5;
    return `${mins} min ${label}`;
  }
  const threshold =
    config[`auto${segment === "warmup" ? "Warmup" : "Cooldown"}Threshold`] ?? 30;
  const minutes = config.sessionDuration || 45;
  const skipped = threshold > 0 && minutes < threshold;
  return skipped ? `no ${label} (short session)` : `${label} auto`;
};

export const timingSummary = (config = {}) => {
  const warm = segmentWord(config, "warmup", "warm-up");
  const cool = segmentWord(config, "cooldown", "cool-down");
  const text = `${warm} · ${cool}`;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const notesSummary = (notes) => {
  const text = String(notes || "").replace(/\s+/g, " ").trim();
  if (!text) return null;
  return text.length > 34 ? `${text.slice(0, 33).trimEnd()}…` : text;
};

/** "Strength + Hard intervals" — the goals in plain words. */
export const goalsLabel = (workoutTypes) => {
  const list = Array.isArray(workoutTypes) ? workoutTypes : [];
  if (list.length === 0) return "Surprise me";
  return list.map((v) => optionLabel(WORKOUT_TYPE_OPTIONS, v)).join(" + ");
};

const READINESS_WORDS = {
  "Low Energy": "low",
  Normal: "normal",
  "Feeling Great": "high",
};

/**
 * The whole request as one sentence, for the desktop summary column. Returns
 * segments so the caller can emphasise the values.
 */
export const sessionSentence = ({ config = {}, shapeName, locationLabel }) => {
  const parts = [];
  const what = shapeName || goalsLabel(config.workoutTypes);
  parts.push({ text: what, strong: true });
  parts.push({ text: ", " });
  parts.push({ text: `${config.sessionDuration || 45} min`, strong: true });
  const format = formatSummary(config);
  if (!format.startsWith("Set by") && format !== "Auto") {
    parts.push({ text: " as " });
    parts.push({ text: format.replace(/^Auto · /, ""), strong: true });
  }
  const muscles = musclesSummary(config.targetAreas);
  if (muscles !== "No focus") {
    parts.push({ text: ", focusing on " });
    parts.push({ text: muscles.toLowerCase(), strong: true });
  }
  if (locationLabel) {
    parts.push({ text: ", at " });
    parts.push({ text: locationLabel, strong: true });
  }
  parts.push({ text: ", " });
  parts.push({
    text: `${READINESS_WORDS[config.readiness] || "normal"} energy`,
    strong: true,
  });
  if (config.noFaff) {
    parts.push({ text: ", " });
    parts.push({ text: "no faff", strong: true });
  }
  parts.push({ text: "." });
  return parts;
};

/**
 * Compact warm-up/cool-down value for the detail row, whose label already
 * says "Warm-up": "Auto, cool-down off" rather than repeating the word.
 */
export const timingRowSummary = (config = {}) => {
  const word = (segment) => {
    const mode = resolveSegmentMode(config, segment);
    if (mode === "off") return "off";
    if (mode === "on") return `${config[`${segment}Duration`] || 5} min`;
    const threshold =
      config[`auto${segment === "warmup" ? "Warmup" : "Cooldown"}Threshold`] ?? 30;
    return threshold > 0 && (config.sessionDuration || 45) < threshold
      ? "skipped"
      : "auto";
  };
  const warm = word("warmup");
  return `${warm.charAt(0).toUpperCase()}${warm.slice(1)}, cool-down ${word("cooldown")}`;
};
